import { useCallback, useEffect, useState } from 'react';
import {
	PROJECT_PICKER_PENDING_CHANGES_LIMIT,
	PROJECT_PICKER_ROWS_PER_PAGE,
	TABLE_DEFAULT_PAGE,
} from '../../common/ui/constants';
import {
	type OnFetchParam,
	type ProjectInput,
	type ProjectPickerFilter,
	type ProjectPickerPage,
	ProjectFilterChoice,
	ProjectPickerState,
} from '../../common/ui/types';
import type { ProjectPickerServiceReturn } from '../../services/project-picker';

export type ProjectPickerControllerParams = ProjectPickerServiceReturn;

const getIsOptionDisabled = (
	pendingChanges: Record<string, boolean>,
	project: ProjectInput,
	isDefaultPriorityScheme: boolean,
) => {
	const { id, isAssociatedWithScheme } = project;

	if (isAssociatedWithScheme && isDefaultPriorityScheme) {
		return {
			disabled: true,
		};
	}

	if (Object.values(pendingChanges).length < PROJECT_PICKER_PENDING_CHANGES_LIMIT) {
		return {
			disabled: false,
		};
	}

	return {
		disabled: pendingChanges[id] === undefined,
	};
};

type PendingChanges = Record<ProjectInput['id'], boolean>;

export type ProjectPickerControllerReturn = {
	currentCountForAssociatedProjects: number;
	filter: ProjectPickerFilter;
	onFetch: (onFetchParam: OnFetchParam) => void;
	onToggleAllProjects: (checked: boolean) => void;
	onToggleProject: (input: ProjectInput) => void;
	newCountForAssociatedProjects: number;
	page: ProjectPickerPage;
	pendingChanges: PendingChanges;
	priorityScheme: {
		name: string;
		isDefault: boolean;
	};
	selectAllChecked: boolean;
	state: (typeof ProjectPickerState)[keyof typeof ProjectPickerState];
};

export const useProjectPickerController = ({
	data,
	onRefetch,
	state,
}: ProjectPickerControllerParams): ProjectPickerControllerReturn => {
	const {
		countForQueriedProjectsInJira,
		countForQueriedAssociatedProjects,
		totalNumberOfAssociatedProjects,
		priorityScheme,
		projects,
	} = data;

	const [currentCountForAssociatedProjects, setCurrentCountForAssociatedProjects] = useState(
		totalNumberOfAssociatedProjects,
	);
	/**
	 * Prop was originally used to display the count of "total projects"
	 * which was deemed confusing, this UI component has been disabled
	 * we should clean up this state and component when we are sure there
	 * is no need for it.
	 */
	const [newCountForAssociatedProjects, setNewCountForAssociatedProjects] = useState(
		totalNumberOfAssociatedProjects,
	);
	const [filter, setFilter] = useState<ProjectPickerFilter>({
		projectFilterChoice: ProjectFilterChoice.ALL_PROJECTS,
		query: '',
	});
	const [page, setPage] = useState<ProjectPickerPage>({
		currentPage: TABLE_DEFAULT_PAGE,
		items: projects,
		totalItems: countForQueriedProjectsInJira,
		pageSize: PROJECT_PICKER_ROWS_PER_PAGE,
	});
	const [pendingChanges, setPendingChanges] = useState<PendingChanges>({});
	const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

	const { query, projectFilterChoice } = filter;
	const { items } = page;

	useEffect(() => {
		setCurrentCountForAssociatedProjects(totalNumberOfAssociatedProjects);
	}, [totalNumberOfAssociatedProjects]);

	useEffect(() => {
		const updatedItems = projects.map((project) => {
			const { checked, id } = project;
			return {
				...project,
				...getIsOptionDisabled(pendingChanges, project, priorityScheme.isDefault),
				checked: id in pendingChanges ? pendingChanges[id] : checked,
			};
		});

		const pendingChangesCount = Object.values(pendingChanges).length;
		const pendingChangesToAddCount = Object.values(pendingChanges).filter((toAdd) => toAdd).length;
		const pendingChangesToRemoveCount = pendingChangesCount - pendingChangesToAddCount;

		setNewCountForAssociatedProjects(
			currentCountForAssociatedProjects - pendingChangesToRemoveCount + pendingChangesToAddCount,
		);

		setPage((prevPage) => ({ ...prevPage, items: updatedItems }));
	}, [currentCountForAssociatedProjects, pendingChanges, projects, priorityScheme.isDefault]);

	useEffect(() => {
		if (projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS) {
			setPage((prevPage) => ({ ...prevPage, totalItems: countForQueriedProjectsInJira }));
		} else if (projectFilterChoice === ProjectFilterChoice.ASSOCIATED_PROJECTS) {
			setPage((prevPage) => ({
				...prevPage,
				totalItems: countForQueriedAssociatedProjects,
			}));
		}
	}, [countForQueriedProjectsInJira, countForQueriedAssociatedProjects, projectFilterChoice]);

	useEffect(() => {
		setSelectAllChecked(items.filter(({ disabled }) => !disabled).some(({ checked }) => checked));
	}, [items]);

	const onFetch = useCallback(
		(onFetchParam: OnFetchParam) => {
			const { projectFilterChoice: newProjectFilter, query: newQuery, selectedPage } = onFetchParam;

			const updatedProjectFilter: (typeof ProjectFilterChoice)[keyof typeof ProjectFilterChoice] =
				newProjectFilter ?? projectFilterChoice;
			const updatedQuery: string = newQuery ?? query;
			const startAtIndex: number =
				(selectedPage ? selectedPage - 1 : 0) * PROJECT_PICKER_ROWS_PER_PAGE;

			onRefetch({
				projectFilter: updatedProjectFilter,
				query: updatedQuery,
				startAt: startAtIndex,
			});

			setFilter({
				projectFilterChoice: updatedProjectFilter,
				query: updatedQuery,
			});
			setPage((prevPage) => ({
				...prevPage,
				currentPage: selectedPage ?? TABLE_DEFAULT_PAGE,
			}));
		},
		[onRefetch, projectFilterChoice, query],
	);

	useEffect(() => {
		if (state === ProjectPickerState.INITITAL) {
			onFetch({
				projectFilterChoice: filter.projectFilterChoice,
				query: filter.query,
				selectedPage: page.currentPage,
			});
		}
	}, [filter.projectFilterChoice, filter.query, onFetch, page.currentPage, state]);

	const onToggleProject = (input: ProjectInput) => {
		const { checked, id, isAssociatedWithScheme } = input;

		const pendingChangeToAdd = !isAssociatedWithScheme && checked;
		const pendingChangeToRemove = isAssociatedWithScheme && !checked;
		const revertChangeToAdd = !isAssociatedWithScheme && !checked;
		const revertChangeToRemove = isAssociatedWithScheme && checked;

		if (pendingChangeToAdd || pendingChangeToRemove) {
			setPendingChanges((prevChanges) => {
				const nextChanges = { ...prevChanges };
				nextChanges[id] = checked;
				return nextChanges;
			});
		} else if (revertChangeToAdd || revertChangeToRemove) {
			setPendingChanges((prevChanges) => {
				const nextChanges = { ...prevChanges };
				delete nextChanges[id];
				return nextChanges;
			});
		}
	};

	const onToggleAllProjects = (checked: boolean) => {
		items.forEach((project) => onToggleProject({ ...project, checked }));
		setSelectAllChecked(checked);
	};

	return {
		currentCountForAssociatedProjects,
		filter,
		onFetch,
		onToggleAllProjects,
		onToggleProject,
		newCountForAssociatedProjects,
		page,
		pendingChanges,
		priorityScheme,
		selectAllChecked,
		state,
	};
};
